<template>
  <app-page class="ds-page" fill lean>
    <!-- Kopf wie Verkäufe, Zahlungen und Saldo (Kundenwunsch 13.10.2026): leichter Titel, Aktionen mit Icon und
         Beschriftung (Handy: Icon-Knöpfe); „Aktualisieren“ ist während des Ladens gesperrt. „Alle auswählen“ bzw.
         „Alle abwählen“ schaltet die Druckauswahl aller Blätter, „Drucken“ druckt die gewählten (ohne Auswahl gesperrt) -->
    <template #actions>
      <template v-if="!handy">
        <app-button icon="refresh" :disabled="loading" @click="load">Aktualisieren</app-button>
        <app-button icon="checkCheck" :disabled="sheets.length === 0" @click="pickAll(!allPicked)">{{ pickAllLabel }}</app-button>
        <app-button icon="printer" :disabled="printableSheets.length === 0" @click="printSheets(printableSheets)">{{ printLabel }}</app-button>
      </template>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" :disabled="loading" @click="load" />
        <app-icon-button icon="checkCheck" :label="pickAllLabel" :disabled="sheets.length === 0" @click="pickAll(!allPicked)" />
        <app-icon-button icon="printer" :label="printLabel" :disabled="printableSheets.length === 0" @click="printSheets(printableSheets)" />
      </template>
    </template>
    <!-- Werkzeugleiste: links der Zeitraum (Tage, mit Schnellwahl), rechts der Filter (Fahrer, Testfahrer) -->
    <template #toolbar>
      <period-field
        days-only
        period-type="daily"
        :from-date="fromDate"
        :date="toDate"
        :presets="presets"
        @apply="onPeriodApply"
      />
    </template>
    <template #toolbar-end>
      <sale-filter-menu
        :value="driverFilters"
        :facets="driverFacets"
        :result-count="sheets.length"
        :description="driverFilterText"
        :segment-defs="testSegments"
        :active-filters="driverFilterCount"
        :result-nouns="resultNouns"
        :empty-value="emptyDriverFilters"
        :show-range="false"
        :align-right="!handy"
        @input="onDriverFilters"
      />
    </template>

    <!-- Vorschau scrollt in sich (fill), darunter die Fußleiste wie bei den Tabellen -->
    <div class="app-grid-fill ds-scroll">
      <v-container class="py-6 ds-preview ds-preview-pad" fluid>
        <div v-if="errorMessage" class="ds-state ds-state--error" role="alert">
          <app-icon name="alertTriangle" :size="16" />
          <span>{{ errorMessage }}</span>
        </div>
        <div v-else-if="!loading && sheets.length === 0" class="ds-state">
          <app-icon name="file" :size="22" class="ds-state__icon" />
          <p class="ds-state__title">Keine Verkäufe im gewählten Zeitraum.</p>
        </div>
        <div v-else-if="loading && sheets.length === 0" class="ds-state" role="status">
          <span class="app-spinner app-spinner--24" aria-hidden="true"></span>
          <p class="ds-state__text">Wird geladen …</p>
        </div>
        <template v-else>
          <div class="ds-preview-grid">
            <div
              v-for="sheet in sheets"
              :key="sheet.user_id"
              class="ds-preview-item"
              :class="{ 'ds-preview-item--aus': !isPicked(sheet) }"
              @click="setPicked(sheet, !isPicked(sheet))"
            >
              <!-- Auswahl für den Druck: runder Knopf oben links auf dem Blatt, nicht gewählt ausgegraut; ein Klick auf
                   das Blatt wählt ebenso (Maus), der Knopf bleibt das Ziel für Tastatur und Screenreader -->
              <button
                type="button"
                class="ds-pick"
                role="checkbox"
                :aria-checked="isPicked(sheet) ? 'true' : 'false'"
                :aria-label="'Drucken: ' + sheet.fullname"
                :title="tooltipOn ? (isPicked(sheet) ? 'Nicht drucken' : 'Drucken') : null"
                @click.stop="setPicked(sheet, !isPicked(sheet))"
              >
                <app-icon name="check" :size="pickIconSize" stroke-width="3" />
              </button>
              <div :ref="`sheet-${sheet.user_id}`" class="ds-sheet">
                <header class="ds-letterhead">
                  <div class="ds-brand">
                    <img v-if="logoUrl" :src="logoUrl" class="ds-logo" alt="">
                    <div v-else class="ds-brand-name">{{ companyName }}</div>
                  </div>
                  <div class="ds-company">
                    <div class="ds-company-name">{{ companyName }}</div>
                    <div v-if="companyUrl">{{ companyUrl }}</div>
                    <div v-if="companyTel">Tel. {{ companyTel }}</div>
                  </div>
                </header>
                <section class="ds-intro">
                  <div class="ds-eyebrow">Verkaufsübersicht</div>
                  <div class="ds-title">{{ sheet.fullname }}</div>
                  <div class="ds-meta">
                    <span><b>Zeitraum</b> {{ formatDate(rangeFrom) }} – {{ formatDate(rangeTo) }}</span>
                    <span v-if="sheet.username"><b>Benutzer</b> {{ sheet.username }}</span>
                    <span class="ds-meta-line"><b>Erstellt</b> {{ createdAt }}</span>
                  </div>
                </section>
                <section class="ds-kpis">
                  <div class="ds-kpi">
                    <span>Verkäufe</span>
                    <strong>{{ sheet.sum.sale_count }}</strong>
                  </div>
                  <div v-if="showCancellations" class="ds-kpi">
                    <span>Stornos</span>
                    <strong>{{ sheet.sum.cancel_count }}</strong>
                  </div>
                  <div v-if="showPaymentSplit" class="ds-kpi">
                    <span>Karte</span>
                    <strong>{{ formatEuro(sheet.sum.card) }}</strong>
                  </div>
                  <div v-if="showVoucher" class="ds-kpi">
                    <span>Gutschein</span>
                    <strong>{{ formatEuro(sheet.sum.voucher || 0) }}</strong>
                  </div>
                  <div class="ds-kpi ds-kpi-main">
                    <span>{{ showPaymentSplit ? 'Summe Bar' : 'Summe' }}</span>
                    <strong>{{ formatEuro(showPaymentSplit ? sheet.sum.cash : sheet.sum.total) }}</strong>
                  </div>
                </section>
                <table class="ds-table">
                  <thead>
                    <tr>
                      <th class="ds-left">Datum</th>
                      <th>Verkäufe</th>
                      <th v-if="showCancellations">Stornos</th>
                      <th v-if="showPaymentSplit">Bar</th>
                      <th v-if="showPaymentSplit">Karte</th>
                      <th v-if="showVoucher">Gutschein</th>
                      <th>Summe</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="day in sheet.days" :key="day.date" :class="{ 'ds-empty': !day.sale_count && !day.cancel_count }">
                      <td class="ds-left">{{ formatDay(day.date) }}</td>
                      <td>{{ day.sale_count || '–' }}</td>
                      <td v-if="showCancellations">{{ day.cancel_count || '–' }}</td>
                      <td v-if="showPaymentSplit">{{ formatEuroOrDash(day.cash, day) }}</td>
                      <td v-if="showPaymentSplit">{{ formatEuroOrDash(day.card, day) }}</td>
                      <td v-if="showVoucher">{{ formatEuroOrDash(day.voucher || 0, day) }}</td>
                      <td>{{ formatEuroOrDash(day.total, day) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td class="ds-left">Gesamt</td>
                      <td>{{ sheet.sum.sale_count }}</td>
                      <td v-if="showCancellations">{{ sheet.sum.cancel_count }}</td>
                      <td v-if="showPaymentSplit">{{ formatEuro(sheet.sum.cash) }}</td>
                      <td v-if="showPaymentSplit">{{ formatEuro(sheet.sum.card) }}</td>
                      <td v-if="showVoucher">{{ formatEuro(sheet.sum.voucher || 0) }}</td>
                      <td>{{ formatEuro(sheet.sum.total) }}</td>
                    </tr>
                  </tfoot>
                </table>
                <footer class="ds-footer">
                  <span>Gute Fahrt wünscht</span>
                  <img :src="simaLogoUrl" class="ds-sima-logo" alt="SIMA">
                  <span>www.simasoftware.de</span>
                </footer>
              </div>
            </div>
          </div>
        </template>
      </v-container>
    </div>
    <app-grid-footer v-if="sheets.length" :pager-always="false">
      <template #summary>
        <span><strong>{{ sheets.length }}</strong> Fahrer</span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span><strong>{{ screenCount(grandTotal.sale_count) }}</strong> Verkäufe</span>
        <span class="app-sep" aria-hidden="true">·</span>
        <span>Gesamt <strong>{{ screenEuro(grandTotal.total) }}</strong></span>
        <span v-if="showPaymentSplit">(Bar {{ screenEuro(grandTotal.cash) }}, Karte {{ screenEuro(grandTotal.card) }}<template v-if="showVoucher">, Gutschein {{ screenEuro(grandTotal.voucher) }}</template>)</span>
        <template v-if="printableSheets.length !== sheets.length">
          <span class="app-sep" aria-hidden="true">·</span>
          <span><strong>{{ printableSheets.length }}</strong> von {{ sheets.length }} zum Druck ausgewählt</span>
        </template>
      </template>
    </app-grid-footer>
  </app-page>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PeriodField from "@/components/sales/PeriodField";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import { formatEuro as formatEuroR6, formatNumber } from "@/utils/format";
import { SHEET_CSS, loadLetterheadLogo, printSheetsHtml, simaPrintLogoUrl } from "@/utils/printSheet";

// Aussehen des Blatts, Briefkopf-Logo und Druck über das iframe: gemeinsam mit der Zusammenfassung der Verkäufe
// (src/utils/printSheet.js)

const euro = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

// Filter-Panel (Kundenwunsch 13.10.2026: „nach diesem prinzip der anderen seiten“): Fahrer als Liste, Testfahrer als
// Umschalter; ohne Fahrer-Auswahl gelten alle angezeigten Fahrer
const TEST_SEGMENTS = [
  { key: "tests", label: "Testfahrer", items: [{ value: null, text: "Ausblenden" }, { value: "zeigen", text: "Anzeigen" }] }
];
const RESULT_NOUNS = ["Fahrer", "Fahrer"];
function emptyDriverFilters() {
  return { tests: null, drivers: [], priceMin: "", priceMax: "" };
}

export default {
  components: { PeriodField, SaleFilterMenu },
  data() {
    const lastWeekStart = moment().subtract(1, "week").startOf("isoWeek");
    return {
      fromDate: lastWeekStart.format("YYYY-MM-DD"),
      toDate: lastWeekStart.clone().endOf("isoWeek").format("YYYY-MM-DD"),
      // Zeitraum der geladenen Daten; die Datumsfelder koennen schon geaendert sein
      rangeFrom: null,
      rangeTo: null,
      createdAt: null,
      drivers: [],
      // nicht angehakte Blätter (user_id); neue Fahrer sind angehakt
      unpicked: [],
      driverFilters: emptyDriverFilters(),
      testSegments: TEST_SEGMENTS,
      resultNouns: RESULT_NOUNS,
      loading: false,
      errorMessage: null,
      requestId: 0,
      customer: this.$store.state.customer,
      logoUrl: null,
    };
  },
  computed: {
    range() {
      return `${this.fromDate}|${this.toDate}`;
    },
    companyName() {
      return (this.customer && this.customer.cust_printname) || this.$store.state.customerName || "";
    },
    companyUrl() {
      const url = this.customer && this.customer.customer_url;
      return url ? url.replace(/^https?:\/\//, "").replace(/\/$/, "") : "";
    },
    companyTel() {
      return (this.customer && this.customer.customer_tel) || "";
    },
    // absolut, damit das Bild auch im Druck-iframe aufgeloest wird
    simaLogoUrl() {
      return simaPrintLogoUrl();
    },
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    tooltipOn() {
      return !this.touch;
    },
    pickIconSize() {
      return this.touch ? 18 : 16;
    },
    showTestUsers() {
      return this.driverFilters.tests === "zeigen";
    },
    selectableDrivers() {
      return this.drivers.filter(d => this.showTestUsers || !d.is_test_user);
    },
    // gewählte Fahrer; ohne Auswahl im Filter alle angezeigten
    selectedUserIds() {
      const chosen = this.driverFilters.drivers;
      const ids = this.selectableDrivers.map(d => d.user_id);
      return chosen.length ? ids.filter(id => chosen.indexOf(String(id)) !== -1) : ids;
    },
    driverFacets() {
      const items = this.selectableDrivers
        .map(d => ({ key: String(d.user_id), text: d.fullname || d.username || String(d.user_id), missing: false }))
        .sort((a, b) => a.text.localeCompare(b.text, "de"));
      return items.length > 1 ? [{ key: "drivers", label: "Fahrer", items }] : [];
    },
    driverFilterCount() {
      return (this.driverFilters.drivers.length ? 1 : 0) + (this.driverFilters.tests ? 1 : 0);
    },
    driverFilterText() {
      const parts = [];
      if (this.driverFilters.drivers.length) {
        parts.push("Fahrer: " + this.sheets.map(s => s.fullname).join(", "));
      }
      if (this.driverFilters.tests) {
        parts.push("Testfahrer angezeigt");
      }
      return parts.length ? parts.join(" · ") : null;
    },
    // Schnellwahl der Zeitraumwahl wie die früheren Knöpfe
    presets() {
      const day = m => m.format("YYYY-MM-DD");
      const lastWeek = moment().subtract(1, "week").startOf("isoWeek");
      return [
        { text: "Letzte Woche", from: day(lastWeek), to: day(lastWeek.clone().endOf("isoWeek")) },
        { text: "Diese Woche", from: day(moment().startOf("isoWeek")), to: day(moment()) },
        { text: "Letzte 7 Tage", from: day(moment().subtract(6, "days")), to: day(moment()) }
      ];
    },
    sheets() {
      const selected = new Set(this.selectedUserIds);
      return this.selectableDrivers
        .filter(d => selected.has(d.user_id))
        .map(d => ({ ...d, days: this.fillDays(d.days) }));
    },
    // angehakte Blätter für „Drucken“
    printableSheets() {
      return this.sheets.filter(s => this.isPicked(s));
    },
    printLabel() {
      return `Drucken (${this.printableSheets.length})`;
    },
    allPicked() {
      return this.sheets.length > 0 && this.printableSheets.length === this.sheets.length;
    },
    pickAllLabel() {
      return this.allPicked ? "Alle abwählen" : "Alle auswählen";
    },
    grandTotal() {
      return this.sheets.reduce((acc, s) => ({
        sale_count: acc.sale_count + s.sum.sale_count,
        cash: acc.cash + s.sum.cash,
        card: acc.card + s.sum.card,
        voucher: acc.voucher + (s.sum.voucher || 0),
        total: acc.total + s.sum.total,
      }), { sale_count: 0, cash: 0, card: 0, voucher: 0, total: 0 });
    },
    // Spalten Bar/Karte bzw. Stornos nur, wenn es im Zeitraum ueberhaupt welche gibt; Gutschein (Drucker-App seit
    // Oktober 2026, Backend liefert voucher) ebenso nur mit Gutscheinen – „Summe Bar“ ist dann ohne Gutscheine
    showPaymentSplit() {
      return this.drivers.some(d => d.sum.card !== 0 || (d.sum.voucher || 0) !== 0);
    },
    showVoucher() {
      return this.drivers.some(d => (d.sum.voucher || 0) !== 0);
    },
    showCancellations() {
      return this.drivers.some(d => d.sum.cancel_count > 0);
    },
  },
  watch: {
    fromDate(value) {
      if (moment(value).isAfter(this.toDate)) this.toDate = value;
    },
    range() {
      this.load();
    },
  },
  mounted() {
    this.load();
    this.loadLetterhead();
  },
  methods: {
    async load() {
      // nur die Antwort der letzten Anfrage uebernehmen
      const requestId = ++this.requestId;
      this.loading = true;
      this.errorMessage = null;
      try {
        const response = await axios.get(
          `${this.$store.state.baseUrl}driversettlement?from_date=${this.fromDate}&to_date=${this.toDate}`
        );
        if (requestId !== this.requestId) return;
        this.drivers = response.data || [];
        this.rangeFrom = this.fromDate;
        this.rangeTo = this.toDate;
        this.createdAt = moment().format("DD.MM.YYYY HH:mm");
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.drivers = [];
        this.errorMessage = (error.response && error.response.data && error.response.data.errorMessage)
          || "Die Verkäufe konnten nicht geladen werden.";
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async loadLetterhead() {
      if (!this.customer) {
        try {
          this.customer = (await axios.get(`${this.$store.state.baseUrl}customer`)).data;
        } catch (error) {
          this.customer = null;
        }
      }
      await this.loadLogo();
    },
    // Briefkopf-Logo: Datei "briefkopf.png" (oder .jpg) aus der Logo-Verwaltung.
    // Die Download-URL ist signiert und laeuft ab, daher vor jedem Druck neu holen.
    async loadLogo() {
      this.logoUrl = await loadLetterheadLogo(this.$store.state.baseUrl);
    },
    emptyDriverFilters() {
      return emptyDriverFilters();
    },
    onDriverFilters(filters) {
      this.driverFilters = Object.assign(emptyDriverFilters(), filters || {});
    },
    isPicked(sheet) {
      return this.unpicked.indexOf(sheet.user_id) === -1;
    },
    // alle angezeigten Blätter wählen bzw. abwählen (Kundenwunsch 13.10.2026)
    pickAll(picked) {
      const shown = this.sheets.map(s => s.user_id);
      const rest = this.unpicked.filter(id => shown.indexOf(id) === -1);
      this.unpicked = picked ? rest : rest.concat(shown);
    },
    setPicked(sheet, picked) {
      const rest = this.unpicked.filter(id => id !== sheet.user_id);
      this.unpicked = picked ? rest : rest.concat(sheet.user_id);
    },
    onPeriodApply(period) {
      this.toDate = period.to;
      this.fromDate = period.from;
    },
    // Jeden Tag des Zeitraums auffuehren, auch ohne Verkauf
    fillDays(days) {
      const byDate = Object.fromEntries(days.map(d => [d.date, d]));
      const result = [];
      for (let day = moment(this.rangeFrom); !day.isAfter(this.rangeTo); day.add(1, "day")) {
        const date = day.format("YYYY-MM-DD");
        result.push(byDate[date] || { date, sale_count: 0, cancel_count: 0, cash: 0, card: 0, voucher: 0, total: 0 });
      }
      return result;
    },
    async printSheets(sheets) {
      await this.loadLogo();
      await this.$nextTick();
      const html = sheets
        .map(s => this.$refs[`sheet-${s.user_id}`][0].outerHTML)
        .join("");
      await printSheetsHtml(html, "Verkäufe Fahrer");
    },
    formatDate(date) {
      return date ? moment(date).format("DD.MM.YYYY") : "";
    },
    formatDay(date) {
      return moment(date).format("dd, DD.MM.YYYY");
    },
    formatEuro(value) {
      return euro.format(value || 0);
    },
    formatEuroOrDash(value, day) {
      return day.sale_count || day.cancel_count ? this.formatEuro(value) : "–";
    },
    // Bildschirm-Zusammenfassung nach R6 (echtes Minus, Tausenderpunkt); das Druckbild nutzt weiter formatEuro
    screenEuro(value) {
      return formatEuroR6(value || 0);
    },
    screenCount(value) {
      return formatNumber(value || 0);
    },
  },
  created() {
    if (!document.getElementById("ds-sheet-css")) {
      const style = document.createElement("style");
      style.id = "ds-sheet-css";
      style.textContent = SHEET_CSS;
      document.head.appendChild(style);
    }
  },
};
</script>

<style scoped>
/* Seitenrand wie Seitenkopf und Werkzeugleiste (20, Tablet hoch 16, Handy 12). Auf dem Handy sind die Blätter
   (mindestens 440 px, ds-preview-grid unverändert) breiter als der Bildschirm: dann scrollt die Vorschau
   waagerecht in sich statt der ganzen Seite */
.ds-preview-pad {
  padding-left: var(--page-x, 20px);
  padding-right: var(--page-x, 20px);
  overflow-x: auto;
}
/* Zustände der Vorschau: Fehler, leer, laden */
.ds-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  color: var(--ink-3);
  text-align: center;
}
.ds-state--error {
  flex-direction: row;
  justify-content: center;
  color: var(--red);
}
.ds-state__icon {
  color: var(--ink-3);
}
p.ds-state__title,
p.ds-state__text {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
}
p.ds-state__title {
  font-weight: 600;
  color: var(--ink);
}
/* Scrollbereich der Vorschau (fill: das Blatt der Seite steht, die Vorschau scrollt in sich); auf dem Handy natürliche
   Höhe. Die Fläche trägt den Hintergrund, damit er auch unter wenigen Blättern bis zur Fußleiste reicht. */
.ds-scroll {
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #f5f5f5;
}
.ds-page.app-page--natural .ds-scroll {
  flex: none;
}
/* linksbündig wie im Druck: die alte globale Regel .container { text-align: center } (legacy-global.css) zentrierte
   Titel und „Erstellt“ nur in der Vorschau */
.ds-preview {
  min-height: 100%;
  background: #f5f5f5;
  text-align: left;
}
/* Dunkelmodus: Vorschaufläche wie Kopfstreifen (--band); auf #f5f5f5 stünde die helle Schrift von Zusammenfassung und
   Zuständen mit 1,1 bis 2,4:1 (auf --band 6,4 bis 13,3:1). Hell bleibt #f5f5f5 (--band wäre dort #f7f8fa). Die Blätter
   bleiben weiß wie Papier und im Druck. */
@media screen {
  html.farbschema-dunkel .ds-scroll,
  html.farbschema-dunkel .ds-preview {
    background: var(--band);
  }
}
/* so viele Blaetter nebeneinander wie passen (Full HD: 3) */
.ds-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(440px, 1fr));
  gap: 24px;
  align-items: start;
}
/* das ganze Blatt ist Klickfläche der Druckauswahl (Kundenwunsch 13.10.2026): Hand-Cursor, kein Markieren von Text
   beim Klicken, bei Hover kräftigerer Schatten und der Hover-Zustand des Knopfs */
.ds-preview-item {
  position: relative;
  min-width: 0;
  cursor: pointer;
  user-select: none;
}
:where(html:not(.dichte-touch)) .ds-preview-item:hover .ds-sheet {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
}
/* Druckauswahl (Kundenwunsch 13.10.2026: „runder knopf und größer und oben links“, „eine border um das selekt feld“,
   „ohne abstand zur farbe sondern direkt am rand“, „border farbe so wie überall anders auch bei den filterbuttons“):
   Kreis 28 (Touch 32, Tippfläche 44) oben links im oberen Blattrand (Vorschau oben 48), Kante 1 --edge direkt an der
   Fläche (Hover --edge-hover) wie Filter- und Feldknöpfe. Gewählt: Fläche --link mit Haken --on-link. Nicht gewählt:
   leerer weißer Kreis mit deutlicherem Schatten, damit er sich vom ausgegrauten Blatt abhebt (Kundenwunsch: „nicht
   ausgrauen sondern deutlich mit weißem bg hervorstechen“); bei Hover ein grauer Haken als Vorschau. */
.ds-pick {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  border: 1px solid var(--edge);
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(21, 26, 33, .18);
  color: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
html.dichte-touch .ds-pick {
  width: 32px;
  height: 32px;
}
html.dichte-touch .ds-pick::after {
  content: '';
  position: absolute;
  top: -7px;
  left: -7px;
  width: 44px;
  height: 44px;
}
:where(html:not(.dichte-touch)) .ds-preview-item:hover .ds-pick {
  border-color: var(--edge-hover);
  color: #8c959f;
}
.ds-pick[aria-checked="true"] {
  background: var(--link);
  box-shadow: var(--sh-ctl);
  color: var(--on-link);
}
:where(html:not(.dichte-touch)) .ds-preview-item:hover .ds-pick[aria-checked="true"] {
  background: var(--link-press);
  color: var(--on-link);
}
/* dunkel: --link ist dort hell (für dunkle Flächen); auf dem weißen Blatt die kräftige Grundfarbe --d-mark (in beiden
   Schemata gleich) mit weißem Haken */
@media screen {
  html.farbschema-dunkel .ds-pick[aria-checked="true"],
  html.farbschema-dunkel .ds-preview-item:hover .ds-pick[aria-checked="true"] {
    background: var(--d-mark);
    color: #fff;
  }
}
.ds-pick:focus {
  outline: none;
}
.ds-pick:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
/* nicht angehakt: Blatt blass und grau (Kästchen bleibt kräftig, damit man es wieder anhakt) */
.ds-preview-item--aus .ds-sheet {
  opacity: 0.45;
  filter: grayscale(1);
}
/* Gruss und SIMA-Logo nur im Druck; das Druck-iframe kennt diese Regel nicht */
.ds-preview .ds-footer {
  display: none;
}
.ds-preview .ds-sheet {
  max-width: none;
  padding: 48px 24px 24px;
  transition: opacity 0.15s, box-shadow 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
</style>
